import { useState } from 'react';
import Button from '~/components/button';
import {
  getProgressiveLeaseContract,
  getProgressivePricingInformation,
  progressiveSubmitCart,
} from '~/modules/payment/progressive/utils/progressive-submit-actions';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import {
  IGetPricing,
  ILeaseLookup,
  IPaymentEstimate,
} from '~/interfaces/commerce-types';
import { numberAsCurrency } from '~/utils/price';
import styles from './progressive-lease-found.module.scss';
import { ProgressiveConfiguration } from '~/gql/__generated__/types';
import { getProgressiveTotal } from '~/modules/payment/progressive/utils';
import {
  PROGRESSIVE_TRANSACTION_TYPE,
  PROGRESSIVE_TENDER_TYPE,
} from '../constants';
import { WMAP_PREFIX } from '../../constants';
interface IProgressiveLeaseFound {
  leaseDetails: ILeaseLookup;
  paymentEstimate: IPaymentEstimate;
  total: number;
  toggleModal: () => void;
  checkoutCart: ISalesOrderResponse;
  progressiveOrderReview: (pricingInfo: IGetPricing) => void;
  progressiveConfigs: ProgressiveConfiguration;
  cartId: string;
}

const ProgressiveLeaseFound: React.FC<IProgressiveLeaseFound> = ({
  leaseDetails,
  paymentEstimate,
  total,
  toggleModal,
  checkoutCart,
  progressiveOrderReview,
  progressiveConfigs,
  cartId,
}) => {
  const [apiInProgress, setApiInProgress] = useState(false);
  const filterMonthsEstimate = paymentEstimate?.TermMonthEstimates?.filter(
    (items) => items.PaymentMonth === 3 || items.PaymentMonth === 6,
  );
  const get12MonthEstimate = paymentEstimate?.TermMonthEstimates?.find(
    (items) => items.PaymentMonth === 12,
  );
  const handleSubmitPayment = async () => {
    setApiInProgress(true);
    const cartTotalAmount = getProgressiveTotal(
      checkoutCart,
      (progressiveConfigs.progressive.progressiveStateDeliveryFee || []).filter(
        (state): state is string => state !== null && state !== undefined,
      ),
      true,
    );

    try {
      const getCartProgressive = await progressiveSubmitCart(
        cartId,
        leaseDetails,
        cartTotalAmount,
        checkoutCart,
        progressiveConfigs?.progressive?.progressiveTenderType ||
          PROGRESSIVE_TENDER_TYPE,
        progressiveConfigs?.progressive?.progressiveTransactionType ||
          PROGRESSIVE_TRANSACTION_TYPE,
      );
      if (getCartProgressive && getCartProgressive.CartId > 0) {
        const details = await getProgressivePricingInformation(
          leaseDetails.ApplicationId!,
          progressiveConfigs?.progressive?.progressiveTenderType ||
            PROGRESSIVE_TENDER_TYPE,
          progressiveConfigs?.progressive?.progressiveTransactionType ||
            PROGRESSIVE_TRANSACTION_TYPE,
        );
        if (details) {
          const lease = await getProgressiveLeaseContract(
            leaseDetails.ApplicationId!,
            progressiveConfigs?.progressive?.progressiveTenderType ||
              PROGRESSIVE_TENDER_TYPE,
            progressiveConfigs?.progressive?.progressiveTransactionType ||
              PROGRESSIVE_TRANSACTION_TYPE,
          );

          if (!lease) {
            throw new Error(
              'Lease contract retrieval failed: ' + JSON.stringify(lease),
            );
          }
          const includeDeliveryFee =
            !!progressiveConfigs.progressive.progressiveStateDeliveryFee?.some(
              (state) => state === checkoutCart.ISOStateCode,
            );

          const combinedDetails = {
            ...lease,
            ...details,
            LeaseDetails: leaseDetails,
            ApprovalAmount: leaseDetails.ApprovalAmount,
            includeDelivery: includeDeliveryFee,
            progressiveCartId: getCartProgressive.CartId,
          };
          progressiveOrderReview(combinedDetails);
        } else {
          throw new Error(
            'Pricing information retrieval failed: ' + JSON.stringify(details),
          );
        }
      } else {
        console.error(
          'Submit cart failed response:',
          getCartProgressive,
          'orderId:',
          checkoutCart?.SalesId || checkoutCart?.QuotationId,
        );
      }
    } catch (error) {
      console.error('ProgressiveLeaseFound:', (error as Error).message);
    } finally {
      setApiInProgress(false);
    }
  };

  const leaseContinueCtaId = `${WMAP_PREFIX}-progressive-lease-continue-btn`;
  const leaseBackButtonCtaId = `${WMAP_PREFIX}-progressive-lease-back-btn`;

  return (
    <section className={styles.container}>
      <h1 className={styles.heading} tabIndex={0}>
        We Found Your Lease
      </h1>
      <div className={styles.approvalsection}>
        <div className="sr-only" tabIndex={0}>
          <div tabIndex={-1}>
            {`Approval Amount. ${numberAsCurrency(leaseDetails?.ApprovalAmount ?? 0)} . Lease. I.D: ${leaseDetails?.ApplicationId?.split('')?.join(',')}`}
          </div>
        </div>
        <div aria-hidden={true}>
          <div className={styles.approvalAmountWrapper}>
            <p>Approval Amount</p>
            <p>{numberAsCurrency(leaseDetails?.ApprovalAmount ?? 0)}</p>
          </div>
          <p className={styles.approvalLeaseIdentity}>
            Lease ID: {leaseDetails?.ApplicationId}
          </p>
        </div>
      </div>
      <p className={styles.supportText} tabIndex={0}>
        {`You’ve been approved to lease items up to 
        ${numberAsCurrency(leaseDetails?.ApprovalAmount || 0)} in cash price with Progressive
        Leasing.`}
      </p>
      <div className={styles.overviewSection}>
        <h2 className={styles.overviewHeading} tabIndex={0}>
          Overview of Your Payments
        </h2>
        <p className={styles.overviewDescription} tabIndex={0}>
          {`How often you're paid and your Mattress Firm cart subtotal make up your payment schedule. You'll automatically be set up for a standard 12-month lease-to-own agreement.`}
        </p>
      </div>
      <div className={styles.paymentOverview}>
        <div className={styles.cartSubTotal} tabIndex={0}>
          <p tabIndex={-1}>Cart Subtotal</p>
          <p tabIndex={-1}>{numberAsCurrency(total || 0)}</p>
        </div>
        <p className={styles.termsHeading} tabIndex={0}>
          12 Month Standard Lease-to-Own
        </p>
        <div className={styles.termsContent} tabIndex={0}>
          <div>
            <p className={styles.termsText}>Every other week payments</p>
            <p className={styles.termsSubText}>excludes estimated tax</p>
          </div>
          <p className={styles.termsTotal} tabIndex={-1}>
            {numberAsCurrency(get12MonthEstimate?.PaymentAmount || 0)}
          </p>
        </div>
        <div className={styles.termsSubTotal} tabIndex={0}>
          <p className={styles.termsSubTotalText}>Total Lease-to-Own Cost</p>
          <p className={styles.termsSubTotalValue}>
            {numberAsCurrency(get12MonthEstimate?.CumulativeAmountPaid || 0)}
          </p>
        </div>
      </div>
      <p className={styles.pill} tabIndex={0}>
        Quick Tip!
      </p>
      <h3 className={styles.earlyPurchaseHeading}>
        {progressiveConfigs.progressive.progressiveLeaseFoundTip?.heading}
      </h3>
      <p className={styles.earlyPurchaseDescription} tabIndex={0}>
        {progressiveConfigs.progressive.progressiveLeaseFoundTip?.text}
      </p>
      {filterMonthsEstimate?.map((estimate, index) => {
        return (
          <div key={index} className={styles.purchaseOptions}>
            <p tabIndex={0} className={styles.purchaseOptionsHeading}>
              {estimate?.PaymentMonth} Month Early Purchase Option
            </p>
            <div className={styles.purchaseOptionsWeekly} tabIndex={0}>
              <p>Every other week payments</p>
              <p>{numberAsCurrency(estimate?.PaymentAmount || 0)}</p>
            </div>
            <div className={styles.purchaseOptionsTotal} tabIndex={0}>
              <p className={styles.purchaseTotalText}>
                Total Lease-to-Own Cost
              </p>
              <p className={styles.purchaseTotalValue}>
                {numberAsCurrency(estimate?.CumulativeAmountPaid || 0)}
              </p>
            </div>
          </div>
        );
      })}
      <div className={styles.ctaSection}>
        <Button
          id={leaseContinueCtaId}
          className={styles.submit}
          loading={apiInProgress}
          onClick={() => {
            handleSubmitPayment().catch((error) => {
              console.error('ProgressiveLeaseFound:', (error as Error).message);
            });
          }}
        >
          {!apiInProgress && 'Continue'}
        </Button>
        <Button
          id={leaseBackButtonCtaId}
          variant="secondary"
          className={styles.cancel}
          onClick={toggleModal}
        >
          Go Back
        </Button>
      </div>
    </section>
  );
};

export default ProgressiveLeaseFound;
